import { useEffect, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Filter, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/services/api';
import type { Paginated } from '@/services/query';
import { Button, Select, Skeleton } from './primitives';
import { FilterBarContext } from './filters';
import { EmptyState, ErrorState } from './feedback';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sortKey?: string;
  className?: string;
  headerClassName?: string;
  hideBelow?: 'sm' | 'md' | 'lg';
  /** Mobile card layout: 'title' = card heading, 'actions' = bottom-right controls, 'hide' = omit. */
  mobile?: 'title' | 'actions' | 'hide';
}

/** Clicks on interactive descendants must not also trigger row navigation. */
function fromControl(e: { target: EventTarget }) {
  return !!(e.target as HTMLElement).closest('button, a, input, select, textarea, label, [role="button"]');
}

function isActionsColumn<T>(c: Column<T>) {
  return c.mobile === 'actions' || c.key === 'actions' || c.key === 'menu' || c.key === 'dl';
}

function MobileCards<T>({ columns, rows, rowKey, onRowClick, loading }: { columns: Column<T>[]; rows?: T[]; rowKey: (r: T) => string; onRowClick?: (r: T) => void; loading?: boolean }) {
  const visible = columns.filter((c) => c.mobile !== 'hide');
  const title = visible.find((c) => c.mobile === 'title') ?? visible.find((c) => !isActionsColumn(c));
  const actions = visible.filter(isActionsColumn);
  const fields = visible.filter((c) => c !== title && !isActionsColumn(c));
  if (loading && !rows?.length) {
    return (
      <div className="space-y-3 p-4" aria-busy>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  return (
    <ul className={cn('divide-y divide-border', loading && 'opacity-60')}>
      {rows?.map((r) => (
        <li
          key={rowKey(r)}
          onClick={onRowClick ? (e) => !fromControl(e) && onRowClick(r) : undefined}
          onKeyDown={onRowClick ? (e) => e.key === 'Enter' && !fromControl(e) && onRowClick(r) : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          className={cn('space-y-3 px-4 py-3.5', onRowClick && 'cursor-pointer active:bg-surface-2 focus:bg-surface-2 focus:outline-none')}
        >
          {title && <div className="min-w-0 text-sm">{title.cell(r)}</div>}
          {fields.length > 0 && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {fields.map((c) => (
                <div key={c.key} className="min-w-0">
                  <dt className="mb-0.5 text-[11px] font-medium text-subtle">{c.header}</dt>
                  <dd className="min-w-0 break-words">{c.cell(r)}</dd>
                </div>
              ))}
            </dl>
          )}
          {actions.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-2.5">
              {actions.map((c) => (
                <div key={c.key}>{c.cell(r)}</div>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

const hide = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' };

export function DataTable<T>({
  columns,
  rows,
  loading,
  error,
  onRetry,
  rowKey,
  onRowClick,
  sortBy,
  sortDir,
  onSort,
  empty,
  className,
  caption,
}: {
  columns: Column<T>[];
  rows?: T[];
  loading?: boolean;
  error?: ApiError | null;
  onRetry?: () => void;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
  empty?: ReactNode;
  className?: string;
  caption?: string;
}) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return (
    <div className={className}>
    <div className="md:hidden">
      <MobileCards columns={columns} rows={rows} rowKey={rowKey} onRowClick={onRowClick} loading={loading} />
    </div>
    <div className="scrollbar-thin hidden overflow-x-auto md:block">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-border bg-surface-2/60 text-left text-xs font-medium text-muted">
            {columns.map((c) => {
              const active = c.sortKey && sortBy === c.sortKey;
              return (
                <th key={c.key} scope="col" className={cn('px-4 py-2.5 font-medium whitespace-nowrap', c.hideBelow && hide[c.hideBelow], c.headerClassName)} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {c.sortKey && onSort ? (
                    <button type="button" className="inline-flex items-center gap-1 hover:text-text" onClick={() => onSort(c.sortKey!, active && sortDir === 'asc' ? 'desc' : 'asc')}>
                      {c.header}
                      {active ? sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading && !rows?.length
            ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-border">
                  {columns.map((c) => (
                    <td key={c.key} className={cn('px-4 py-3', c.hideBelow && hide[c.hideBelow])}>
                      <Skeleton className="h-4 w-full max-w-[160px]" />
                    </td>
                  ))}
                </tr>
              ))
            : rows?.map((r) => (
                <tr
                  key={rowKey(r)}
                  onClick={onRowClick ? (e) => !fromControl(e) && onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => e.key === 'Enter' && !fromControl(e) && onRowClick(r) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn('border-b border-border transition-colors last:border-0', onRowClick && 'cursor-pointer hover:bg-surface-2 focus:bg-surface-2 focus:outline-none', loading && 'opacity-60')}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cn('px-4 py-3 align-middle', c.hideBelow && hide[c.hideBelow], c.className)}>
                      {c.cell(r)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
      {!loading && rows && rows.length === 0 && (empty ?? <EmptyState title="No records found" description="Try adjusting your filters." />)}
    </div>
  );
}

export function Pagination({ data, onPage, onPageSize }: { data?: Pick<Paginated<unknown>, 'page' | 'pageSize' | 'totalItems' | 'totalPages'>; onPage: (p: number) => void; onPageSize?: (s: number) => void }) {
  if (!data) return null;
  const from = data.totalItems ? (data.page - 1) * data.pageSize + 1 : 0;
  const to = Math.min(data.page * data.pageSize, data.totalItems);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-muted">
      <span aria-live="polite">
        {from}–{to} of <span className="font-medium text-text tabular-nums">{data.totalItems}</span>
      </span>
      <div className="flex items-center gap-2">
        {onPageSize && (
          <label className="flex items-center gap-2">
            <span className="hidden sm:inline">Rows</span>
            <Select value={data.pageSize} onChange={(e) => onPageSize(Number(e.target.value))} className="h-8 w-20 text-xs" aria-label="Rows per page">
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </label>
        )}
        <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => onPage(data.page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="tabular-nums">
          {data.page} / {data.totalPages}
        </span>
        <Button variant="outline" size="sm" disabled={data.page >= data.totalPages} onClick={() => onPage(data.page + 1)} aria-label="Next page">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** Debounced compact search box. */
export function SearchInput({ value, onChange, placeholder = 'Search…', className }: { value?: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  useEffect(() => {
    if ((value ?? '') === v) return;
    const t = setTimeout(() => onChange(v), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
  return (
    <div className={cn('relative w-full sm:w-52', className)}>
      <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        type="search"
        className="h-9 w-full rounded-lg border border-border bg-surface pr-3 pl-9 text-[13px] text-text placeholder:text-subtle transition-colors hover:border-subtle focus:border-muted focus:outline-none"
      />
    </div>
  );
}

/** Filter row; on phones the controls collapse behind a "Filters" toggle so lists stay visible. */
export function FilterBar({ children, onReset, activeCount = 0 }: { children: ReactNode; onReset?: () => void; activeCount?: number }) {
  const [open, setOpen] = useState(false);
  const reset = onReset && activeCount > 0 && (
    <Button variant="ghost" size="sm" onClick={onReset} icon={<RotateCcw className="h-3.5 w-3.5" />}>
      Reset
      <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-1.5 text-[10px] text-primary">
        <Filter className="h-2.5 w-2.5" />
        {activeCount}
      </span>
    </Button>
  );
  return (
    <FilterBarContext.Provider value={true}>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 sm:hidden">
          <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open} icon={<SlidersHorizontal className="h-3.5 w-3.5" />}>
            {open ? 'Hide filters' : 'Filters'}
            {activeCount > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-fg tabular-nums">{activeCount}</span>}
          </Button>
          {reset}
        </div>
        <div className={cn('flex-wrap items-center gap-2 sm:flex', open ? 'mt-3 flex' : 'hidden')}>
          {children}
          <span className="hidden sm:contents">{reset}</span>
        </div>
      </div>
    </FilterBarContext.Provider>
  );
}

export { FilterSelect } from './filters';
